import {createModel} from '@rematch/core';

import {
  TextPromotionPayload,
  UrlPromotionsType,
  checkUrl,
  createCommonPromotion,
} from '~/services/hot-api/promotions';
import {getProfileByContacts} from '~/services/promotions';
import {getUrlInfo} from '~/services/hotsearch/promotions';
import {PromotionTextTypes} from '~/utils/constants';

import type {RootModel} from '..';

export interface TextPromotionsState {
  mainForm: {
    url: string;
    displayUrl: string;
    headline: string;
    description: string;
  };
  bids: {
    cpc: string;
    daily: string;
  };
  selectedLocations: number[];
  selectedCategories: number[];
  checkUrlResult: string | null;
}

const defaultState: TextPromotionsState = {
  mainForm: {
    url: '',
    displayUrl: '',
    headline: '',
    description: '',
  },
  bids: {
    cpc: '',
    daily: '',
  },
  checkUrlResult: null,
  selectedLocations: [],
  selectedCategories: [],
};

const getPromotionPayload = (state: TextPromotionsState): TextPromotionPayload => {
  const {selectedLocations, selectedCategories, bids, mainForm} = state;

  return {
    promo: PromotionTextTypes.Text,
    details: {
      title: mainForm.headline,
      description: mainForm.description,
      url: mainForm.url,
      display_url: mainForm.displayUrl,
    },
    bids: {
      price: parseFloat(bids.cpc),
      daily_limit: parseFloat(bids.daily),
    },
    category_ids: selectedCategories,
    geo: {
      city_ids: selectedLocations,
      county_ids: [],
      state_ids: [],
      country_ids: [],
    },
  };
};

export const textPromotion = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setPayload: (state: TextPromotionsState, payload: Partial<TextPromotionsState>) => ({
      ...state,
      ...payload,
    }),
  },
  effects: (dispatch) => {
    return {
      resetMainStep: () => {
        dispatch.urlPromotion.setPayload({
          type: null,
          profile: null,
          content: {
            website: '',
            headline: '',
            description: '',
          },
          selectedCategories: [],
          selectedLocations: [],
        });
      },
      mainFormSubmit: async ({url, onlyfans}: {url: string; onlyfans: string}) => {
        if (url) {
          const urlResponse = await checkUrl(url);

          const isSuccess = urlResponse.data?.status === 'OK';

          if (isSuccess) {
            const urlInfoResponse = await getUrlInfo(url);
            const website = urlInfoResponse.data;

            dispatch.urlPromotion.setPayload({
              type: UrlPromotionsType.Url,
              profile: website,
              content: {
                website: url,
                headline: website?.title || '',
                description: website?.description || '',
              },
              selectedCategories: website?.site_categories || [],
              selectedLocations: website?.site_geos || [],
            });
          }

          return {
            type: UrlPromotionsType.Url,
            response: urlResponse.data,
            isSuccess,
          };
        }

        if (onlyfans) {
          const profileResponse = await getProfileByContacts({
            onlyfans,
          });

          const isSuccess = profileResponse.data?.profiles?.length > 0;
          const profile = profileResponse.data?.profiles[0];

          if (isSuccess) {
            dispatch.urlPromotion.setPayload({
              type: UrlPromotionsType.Onlyfans,
              profile,
              content: {
                website: profile.profile_url,
                headline: profile.title || '',
                description: profile.description || '',
              },
            });
          }

          return {
            type: UrlPromotionsType.Onlyfans,
            response: profileResponse.data,
            isSuccess,
          };
        }

        return {
          type: '',
          isSuccess: false,
          response: null,
        };
      },
      createPromotion: async (_payload: void, state) => {
        const promotionPayload = getPromotionPayload(state.textPromotion);

        const response = await createCommonPromotion(promotionPayload);

        return response.data;
      },
    };
  },
});
